import type { PostInsights, PostMetricAverages } from "@nlc/shared-types";

import { twMerge } from "tailwind-merge";

import { formatMetric } from "../formatMetric";
import { percentDifference } from "./percentDifference";

const METRICS: Array<{
  description: string;
  key: "comments" | "likes" | "saved" | "shares";
  label: string;
}> = [
  { description: "People who liked this post", key: "likes", label: "Likes" },
  {
    description: "Comments left on this post",
    key: "comments",
    label: "Comments",
  },
  {
    description: "Times this post was shared",
    key: "shares",
    label: "Shares",
  },
  {
    description: "People who saved this post for later",
    key: "saved",
    label: "Saves",
  },
];

export function InteractionsComparison({
  averages,
  insights,
}: {
  averages: PostMetricAverages;
  insights: null | PostInsights;
}) {
  return (
    <section className="flex flex-col gap-xl rounded-2xl border border-border-secondary bg-bg-primary p-2xl">
      <div className="flex flex-col gap-xs">
        <h3 className="text-lg font-semibold text-text-primary">
          Interactions
        </h3>
        <p className="text-sm text-text-tertiary">
          What people did after seeing this post.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-medium text-text-tertiary">
            <th className="pb-md font-medium" scope="col">
              Metric
            </th>
            <th className="pb-md text-right font-medium" scope="col">
              This post
            </th>
            <th className="pb-md text-right font-medium" scope="col">
              Your average
            </th>
            <th className="pb-md text-right font-medium" scope="col">
              Difference
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-secondary border-t border-border-secondary">
          {METRICS.map(({ description, key, label }) => {
            const value = insights?.[key];
            const average = averages[key];
            const difference = percentDifference(value, average);

            return (
              <tr key={key}>
                <th className="py-lg pr-md text-left font-normal" scope="row">
                  <span className="block font-semibold text-text-primary">
                    {label}
                  </span>
                  <span className="text-text-tertiary">{description}</span>
                </th>
                <td className="py-lg text-right font-semibold text-text-primary">
                  {formatMetric(value)}
                </td>
                <td className="py-lg text-right text-text-secondary">
                  {formatMetric(average === null ? null : Math.round(average))}
                </td>
                <td
                  className={twMerge(
                    "py-lg text-right font-medium",
                    difference === null && "text-text-quaternary",
                    difference !== null &&
                      (difference >= 0
                        ? "text-text-success-primary"
                        : "text-text-error-primary"),
                  )}
                >
                  {difference === null
                    ? "—"
                    : `${difference > 0 ? "+" : ""}${difference}%`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-sm text-text-tertiary">
        Numbers come from Instagram and Facebook and refresh a few times a day.
      </p>
    </section>
  );
}
